import type { ReactNode } from "react";
import { tablePadding, tableSpacing } from "@core/table";
import { tokens } from "@core/theme/tokens";
import { EmptyState, RowIconAction, type EmptyStateKind } from "@portal/common";

// Piezas de tabla compartidas entre `MemberRulesPanel` y
// `ReferralRulesPanel`: la tarjeta contenedora, la fila de encabezado, la
// fila de datos y los tres estados de carga eran clones literales entre
// ambos paneles (jscpd, quality-gate) — extraídos acá sin cambiar ningún
// valor visual del prototipo (admin.jsx AdminMotorMiembro/AdminMotorReferido
// comparten la misma composición de tabla).
export const ruleTableCardStyle = {
  background: tokens.surface.surface,
  border: `1px solid ${tokens.surface.line}`,
  borderRadius: 18,
} as const;

// Mismos meses y misma forma que `fISO` del prototipo (admin.jsx:195-199:
// `MESES` + día sin cero inicial + mes abreviado + año, p. ej. "1 ene
// 2026") — el look es el prototipo, no un formato propio.
const RULE_DATE_MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

// `validFrom`/`validUntil` son `format: date` (calendario puro, sin hora ni
// zona) — NUNCA pasan por `new Date(value)` + `toLocaleDateString`: eso
// interpreta la cadena como medianoche UTC y, con el `timezoneId` del
// contrato visual (America/Guayaquil, UTC-5), la corre un día hacia atrás
// (finding real: "2026-01-01" se veía "31/12/2025" en el spec visual). Una
// fecha de calendario se reformatea con las cifras que ya trae, sin Date.
export function formatRuleDate(value: string | null | undefined): string {
  if (!value) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return "—";
  const [, year, month, day] = match;
  const dayNumber = Number.parseInt(day!, 10);
  const monthName = RULE_DATE_MONTHS[Number.parseInt(month!, 10) - 1];
  if (!monthName || Number.isNaN(dayNumber)) return "—";
  return `${dayNumber} ${monthName} ${year}`;
}

export function RuleTableHeaderRow({
  columns,
  headers,
  trailing,
}: {
  columns: string;
  headers: string[];
  trailing?: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: columns,
        gap: tableSpacing.columnGap,
        alignItems: "center",
        padding: tablePadding("18px", "11px"),
        borderBottom: `1px solid ${tokens.surface.line}`,
      }}
    >
      {headers.map((header, index) => (
        <span
          key={`${header}-${index}`}
          className="label"
          style={{
            font: `600 11px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          {header}
        </span>
      ))}
      {trailing ? (
        <span
          className="label"
          style={{
            textAlign: "right",
            font: `600 11px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          {trailing}
        </span>
      ) : null}
    </div>
  );
}

export function RuleTableRow({
  columns,
  active,
  children,
}: {
  columns: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: columns,
        gap: tableSpacing.columnGap,
        alignItems: "center",
        padding: tablePadding("13px"),
        borderBottom: `1px solid ${tokens.surface.line}`,
        opacity: active ? 1 : 0.55,
      }}
    >
      {children}
    </div>
  );
}

// El badge Activa/Inactiva y el gate del botón Editar eran clones literales
// entre `MemberRulesPanel` y `ReferralRulesPanel` (jscpd, quality-gate,
// intento 4 dup:check) — mismo criterio que el resto de este archivo:
// extraídos sin cambiar ningún valor visual ni el gate de permiso
// (F1, retry-category review_finding, 2026-09-07: `canOpenEditor` sigue
// siendo `canEdit || canInactivate`, decidido por quien llama).
export function RuleStatusBadge({ active }: { active: boolean }) {
  return (
    <span className={"stat stat-" + (active ? "gold" : "neutral")}>
      {active ? "Activa" : "Inactiva"}
    </span>
  );
}

export function RuleEditAction({
  visible,
  onEdit,
}: {
  visible: boolean;
  onEdit: () => void;
}) {
  if (!visible) return null;
  return <RowIconAction label="Editar" icon="edit" onClick={onEdit} />;
}

export function RuleTableStates({
  isLoading,
  isError,
  isEmpty,
  emptyKind = "no-data",
}: {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  emptyKind?: EmptyStateKind;
}) {
  if (isLoading) return <div style={{ padding: 24 }}>Cargando…</div>;
  if (isError) {
    return (
      <div style={{ padding: 24 }}>
        <EmptyState kind="error" />
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div style={{ padding: 24 }}>
        <EmptyState kind={emptyKind} />
      </div>
    );
  }
  return null;
}
